import NavigableModal from '@components/NavigableModal/NavigableModal';
import { fieldValueAtom } from '@components/listings-search-bar/atoms';
import { isOnSemanticSearchInExperimentAtom } from '@components/listings-search-bar/atoms';
import useAnalyticsContext from '@hooks/useAnalyticsContext';
import { useAppDispatch, useAppSelector } from '@redux/hooks';
import { CtaButton } from '@tkww/orion-web-ds-action';
import LocationTypeahead from '@xo-union/tk-component-location-typeahead';
import { ModalHeader } from '@xo-union/tk-component-modals';
import { useAtomValue } from 'jotai';
import debounce from 'lodash/debounce';
import React, { useState } from 'react';
import { buildSearchRef } from '../../../../components/shared/Search/helpers';
import { updateLocation } from '../../../../redux/location';
import { setCookieLocation } from '../../../../utils/cookie/location';
import locationToString from '../../../../utils/locationToString';
import {
	CATEGORY_LANDING_PAGE,
	CHANGE_LOCATION,
} from '../../../../utils/tracking';
import { useTrackCatGeo } from '../../../VendorsSearch/containers/SemanticSearch/hooks/use-track-cat-geo';
import Styles from './styles.scss';

export interface ChangeLocationModalProps {
	headerText?: string;
	closeCallback: () => void;
	isModalOpen: boolean;
}

export const ChangeLocationModal = ({
	closeCallback,
	headerText,
	isModalOpen,
}: ChangeLocationModalProps) => {
	const [locationHash, setLocationHash] = useState<Union.Location | null>(null);
	const { track } = useAnalyticsContext();
	const dispatch = useAppDispatch();
	const location = useAppSelector((state) => state.location);
	const categories = useAppSelector((state) => state.categories);
	const categoryCode = useAppSelector((state) => state.category.code);
	const trackCatGeo = useTrackCatGeo();
	const searchTerm = useAtomValue(fieldValueAtom);
	const isOnSemanticSearchInExperiment = useAtomValue(
		isOnSemanticSearchInExperimentAtom,
	);

	const debouncedTrackLocationChange = debounce(
		(searchString: string) => trackCatGeo('typing', { searchString }),
		1000,
	);

	const header = headerText || 'My Wedding Location';

	const searchRef = buildSearchRef(categories, categoryCode, locationHash);

	const checkUserInput = (e: React.ChangeEvent<HTMLInputElement>) => {
		debouncedTrackLocationChange(e.target.value);
		if (locationHash !== null) {
			setLocationHash(null);
		}
	};

	const trackLocationChange = () => {
		track({
			event: CHANGE_LOCATION,
			properties: {
				sourcePage: isOnSemanticSearchInExperiment
					? 'semantic_results'
					: CATEGORY_LANDING_PAGE,
			},
		});
	};

	const updateSearch = () => {
		dispatch(updateLocation(locationHash, location.ip));
		setCookieLocation(locationHash);
		trackLocationChange();

		if (searchTerm?.length > 0) {
			const url = new URL(`${window.location.origin}${searchRef}`);

			url.searchParams.set('q', searchTerm);

			window.location.href = url.toString();
		} else {
			window.location.href = searchRef;
		}

		trackCatGeo('search_completed');
		// No need for closeCallback since we are redirecting
		// and it causes syncPage() on listings to not work correctly for searching
		return;
	};

	const handleLocationSelection = (locationHash: Union.Location | null) => {
		trackCatGeo('location_selected', {
			vendorMarket: locationToString(locationHash),
		});
		setLocationHash(locationHash);
	};

	return (
		<NavigableModal
			size="md"
			className={Styles.modal}
			isModalOpen={isModalOpen}
			closeCallback={closeCallback}
		>
			<ModalHeader>{header}</ModalHeader>
			<div className={Styles.modalBody}>
				<div className={Styles.fieldWrapper}>
					<LocationTypeahead
						onChange={handleLocationSelection}
						onInput={(e) => checkUserInput(e)}
						label="City, State"
					/>
				</div>
				<div className={Styles.buttonContainer}>
					<CtaButton
						size="md"
						disabled={locationHash === null}
						onClick={updateSearch}
					>
						Search
					</CtaButton>
				</div>
			</div>
		</NavigableModal>
	);
};
